import { useEffect, useMemo, useState } from 'react';

type UseScrollSpyOptions = {
  root?: HTMLElement | null;
  rootMargin?: string;
  threshold?: number | number[];
};

/**
 * Tracks which section is currently visible using IntersectionObserver.
 * Intended for in-page navigation (scroll-to + active highlight).
 */
export function useScrollSpy(sectionIds: string[], options?: UseScrollSpyOptions) {
  const [activeSectionId, setActiveSectionId] = useState<string | null>(null);

  const stableSectionIds = useMemo(
    () => sectionIds.filter(Boolean),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(sectionIds)]
  );

  useEffect(() => {
    if (stableSectionIds.length === 0) return;

    const elements = stableSectionIds
      .map((id) => document.getElementById(id))
      .filter((el): el is HTMLElement => el !== null);

    if (elements.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => (a.boundingClientRect.top ?? 0) - (b.boundingClientRect.top ?? 0));

        const firstVisible = visible[0]?.target as HTMLElement | undefined;
        if (firstVisible?.id) {
          setActiveSectionId(firstVisible.id);
        }
      },
      {
        root: options?.root ?? null,
        // Treat a section as "active" shortly after it enters near the top.
        rootMargin: options?.rootMargin ?? '-20% 0px -70% 0px',
        threshold: options?.threshold ?? [0, 0.1, 0.25],
      }
    );

    for (const el of elements) observer.observe(el);
    return () => observer.disconnect();
  }, [stableSectionIds, options?.root, options?.rootMargin, options?.threshold]);

  return activeSectionId;
}
